// API client PRD 3 — calls + admin audio/video
import api from "./api";
import type {
  CallSession,
  EstadoDaLigacao,
  InitiateCallRequest,
  InitiateCallResponse,
  JoinCallResponse,
  LigacaoAtivaNaSala,
  RecusarResponse,
  CallRecording,
  AVProfile,
} from "@/types/calls";

// ── Ligações do chat que tocam do outro lado (24/10/2026) ──────────────────
// Funções soltas, não membros do `callsService`: quem as usa (o CallProvider, a faixa "Entrar" do
// grupo) importa só a que precisa, e o `GET /calls/{id}/estado` e o `/sala/{id}/ativa` leem a
// PRÓPRIA ligação (sem `calls:read`, que o Colaborador não tem). Erro passa como veio do axios:
// 404 = a pessoa não vê a ligação (ou ela não existe), 409 = já parou de tocar.

/** Recusa a ligação que está tocando para esta pessoa (para o toque em todos os aparelhos dela). */
export async function recusarLigacao(sessionId: string): Promise<RecusarResponse> {
  const r = await api.post<RecusarResponse>(`/calls/${sessionId}/recusar`);
  return r.data;
}

/** O estado da própria ligação: tocando, em curso ou encerrada (reconexão e link `?ligacao=`). */
export async function estadoDaLigacao(sessionId: string): Promise<EstadoDaLigacao> {
  const r = await api.get<EstadoDaLigacao>(`/calls/${sessionId}/estado`);
  return r.data;
}

/** A ligação em curso nesta conversa, ou `null` se não há (a faixa "Entrar" do grupo). */
export async function ligacaoAtivaNaSala(roomId: string): Promise<LigacaoAtivaNaSala | null> {
  const r = await api.get<LigacaoAtivaNaSala | null>(`/calls/sala/${roomId}/ativa`);
  // O servidor responde 200 com `null` quando não há ligação.
  return r.data || null;
}

// ── Sair do sistema no meio da ligação (R4, sobras das ligações) ───────────────────────────────────────────
// O `logout()` apaga o token do storage de forma síncrona, então o `api` (axios) já não o teria quando o
// provider desmonta: este pedido leva o token de QUEM SAI, capturado antes da limpeza, num `fetch` direto
// com `keepalive` (a navegação ou o fechamento da aba não o cancela). Mesma base do `AuthContext`:
// `VITE_API_URL` já traz o `/api/v1`, o caminho nunca o repete.
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

/**
 * Encerra no servidor a ligação de quem está saindo do sistema: `/end` na conversa direta (encerra para os
 * dois), `/leave` no grupo (só tira a pessoa). Melhor-esforço e sem esperar: devolve na hora e engole
 * qualquer falha (rede, 401, 409 de ligação que já acabou).
 */
export function encerrarLigacaoAoSair(token: string, alvo: { id: string; grupo: boolean }): void {
  const caminho = alvo.grupo ? "leave" : "end";
  void fetch(`${API_BASE_URL}/calls/${alvo.id}/${caminho}`, {
    method: "POST",
    keepalive: true,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    // Só o `/end` lê corpo (o motivo, o mesmo "normal" do `useCall.end`); o `/leave` não declara nenhum.
    ...(alvo.grupo ? {} : { body: JSON.stringify({ reason: "normal" }) }),
  }).catch(() => undefined);
}

export const callsService = {
  initiate: async (data: InitiateCallRequest) => {
    // `/calls/initiate` — a rota do backend. Era `"/calls"`, que não existe
    // (07/08/2026): não há handler POST na raiz do prefixo, então toda tentativa
    // de iniciar chamada morria em 405/404. Confirmado listando as rotas do app.
    const r = await api.post<InitiateCallResponse>("/calls/initiate", data);
    return r.data;
  },

  join: async (sessionId: string) => {
    const r = await api.post<JoinCallResponse>(`/calls/${sessionId}/join`);
    return r.data;
  },

  leave: async (sessionId: string) => {
    const r = await api.post(`/calls/${sessionId}/leave`);
    return r.data;
  },

  end: async (sessionId: string) => {
    const r = await api.post(`/calls/${sessionId}/end`);
    return r.data;
  },

  startRecording: async (sessionId: string) => {
    const r = await api.post<CallRecording>(`/calls/${sessionId}/recording/start`);
    return r.data;
  },

  stopRecording: async (sessionId: string) => {
    const r = await api.post(`/calls/${sessionId}/recording/stop`);
    return r.data;
  },

  promoteToSFU: async (sessionId: string) => {
    const r = await api.post(`/calls/${sessionId}/promote-to-sfu`);
    return r.data;
  },

  refreshTurn: async (sessionId: string) => {
    const r = await api.post(`/calls/${sessionId}/refresh-turn`);
    return r.data;
  },

  listSessions: async (params?: { status?: string; limit?: number }) => {
    const r = await api.get<CallSession[]>("/calls", { params });
    return r.data;
  },

  getSession: async (sessionId: string) => {
    const r = await api.get<CallSession>(`/calls/${sessionId}`);
    return r.data;
  },

  listRecordings: async (params?: { call_session_id?: string }) => {
    const r = await api.get<CallRecording[]>("/calls/recordings", { params });
    return r.data;
  },
};

export const audioVideoAdminService = {
  listProfiles: async () => {
    const r = await api.get<AVProfile[]>("/admin/audio-video/profiles");
    return Array.isArray(r.data) ? r.data : (r.data as any)?.items || [];
  },

  getActiveProfile: async () => {
    const r = await api.get<AVProfile>("/admin/audio-video/profiles/active");
    return r.data;
  },

  createProfile: async (data: Partial<AVProfile>) => {
    const r = await api.post<AVProfile>("/admin/audio-video/profiles", data);
    return r.data;
  },

  switchProfile: async (profileId: string) => {
    const r = await api.post(`/admin/audio-video/profiles/${profileId}/switch`);
    return r.data;
  },

  getProfileHealth: async (profileId: string) => {
    const r = await api.get(`/admin/audio-video/profiles/${profileId}/health`);
    return r.data;
  },
};
